import { api } from './client';
import type { VenueProposal, DayPlanProposal } from './ai.api';

// ─── Raw plan the extraction returns (mirrors server extractTripPlanSchema) ──

export interface ImportedActivity {
  title: string; type: string; startTime?: string; durationMins?: number;
  venueName?: string; zone?: string; address?: string; bookingUrl?: string; notes?: string;
}
export interface ImportedDay { dayNumber?: number; date?: string; activities: ImportedActivity[]; }
export interface ImportedVenue { name: string; category: string; zone?: string; address?: string; bookingUrl?: string; notes?: string; }
export interface ImportedFlightLeg { fromAirport: string; toAirport: string; departureTime: string; arrivalTime: string; flightNumber?: string; airline?: string; }
export interface ImportedFlight { direction?: 'OUTBOUND' | 'RETURN'; bookingRef?: string; passengerNames?: string[]; legs: ImportedFlightLeg[]; bookingUrl?: string; notes?: string; }
export interface ImportedStay { name: string; address?: string; checkIn: string; checkOut?: string; nights?: number; bookingRef?: string; bookingUrl?: string; notes?: string; }
export interface ImportedBooking { type?: string; title: string; date: string; startTime?: string; endTime?: string; venue?: string; address?: string; bookingRef?: string; bookingUrl?: string; notes?: string; }
export interface ImportedTip { category: string; content: string; }
export interface ImportedPacking { section: string; items: string[]; }
export interface ImportedExpense { description: string; amount: number; currency?: string; category?: string; date?: string; notes?: string; }

export interface ExtractedTripPlan {
  destination?: string; tripName?: string; startDate?: string; endDate?: string;
  venues: ImportedVenue[]; days: ImportedDay[];
  flights: ImportedFlight[]; stays: ImportedStay[]; bookings: ImportedBooking[];
  tips: ImportedTip[]; packing: ImportedPacking[]; expenses: ImportedExpense[];
}

export interface ExtractTripPlanResponse {
  plan: ExtractedTripPlan;
  aiCreditsLeft: number;
  premiumAiCreditsLeft: number;
  /** Language of the document, or null when it has too few words to tell. */
  documentLanguage: string | null;
}

// ─── Materialized, ready-to-persist proposals (each carries a pendingId) ─────

export interface FlightProposal { pendingId: string; direction?: 'OUTBOUND' | 'RETURN'; bookingRef?: string; passengerNames?: string[]; legs: ImportedFlightLeg[]; bookingUrl?: string; notes?: string; }
// `nights` is derived server-side from the resolved range — the review list shows
// it because a multi-night stay reading as one night is the mistake worth catching.
export interface HotelProposal { pendingId: string; name: string; address?: string; checkIn: string; checkOut: string; nights: number; bookingRef?: string; bookingUrl?: string; notes?: string; }
export interface BookingProposal { pendingId: string; type?: string; title: string; date: string; startTime?: string; endTime?: string; venue?: string; address?: string; bookingRef?: string; bookingUrl?: string; notes?: string; }
export interface TipProposal { pendingId: string; category: string; content: string; }
export interface PackingProposal { pendingId: string; section: string; items: string[]; }
export interface ExpenseSplit { userId: string; amount: number; }
export interface ExpenseProposal {
  pendingId: string; description: string; category: string; amount: number; currency: string;
  paymentMethod: 'CASH' | 'CARD' | 'TRANSFER'; status: 'PAID' | 'PENDING'; date: string;
  notes?: string | null; splits: ExpenseSplit[];
}

export interface MaterializeResponse {
  venues: VenueProposal[];
  dayPlans: DayPlanProposal[];
  flights: FlightProposal[];
  hotels: HotelProposal[];
  bookings: BookingProposal[];
  tips: TipProposal[];
  packing: PackingProposal[];
  expenses: ExpenseProposal[];
  mapsQuotaExceeded: { resetsAt?: string } | null;
  /** Set when the document speaks a language the trip doesn't — the review step offers to translate. */
  languageMismatch: { document: string; trip: string } | null;
}

export const importApi = {
  // One AI call: reads the document(s), returns the structured plan (no persistence).
  // The client extracts one document per request (see extractAllWithProgress) so
  // it can show real progress; `consumeCredit` charges the single import credit
  // only on the first request.
  // ADR-0034: staff importing for their agency charge the agency's AI allowance.
  extract: (files: File[], opts: { consumeCredit?: boolean; agencyId?: string } = {}) => {
    const form = new FormData();
    files.forEach(file => form.append('files', file));
    if (opts.consumeCredit === false) form.append('consumeCredit', 'false');
    if (opts.agencyId) form.append('agencyId', opts.agencyId);
    return api.post<ExtractTripPlanResponse>(
      '/trips/import-extract',
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    ).then(r => r.data);
  },

  // Maps onto the trip's days, enriches place coordinates and computes expense
  // splits, returning ready-to-persist proposals per category. No AI call unless
  // `translate` is set, which rewrites the plan's prose into the trip's own
  // language first (place names, refs and dates are never touched).
  materialize: (tripId: string, plan: ExtractedTripPlan, opts: { autoAccept?: boolean; translate?: boolean } = {}) =>
    api.post<MaterializeResponse>(
      `/trips/${tripId}/ai/import-materialize`,
      { plan, autoAccept: opts.autoAccept ?? false, translate: opts.translate ?? false },
    ).then(r => r.data),
};

const EMPTY_PLAN: ExtractedTripPlan = {
  venues: [], days: [], flights: [], stays: [], bookings: [], tips: [], packing: [], expenses: [],
};

/** Union of several per-document plans: first non-empty scalar wins, arrays concat. */
export function mergeImportedPlans(plans: ExtractedTripPlan[]): ExtractedTripPlan {
  const firstStr = (get: (p: ExtractedTripPlan) => string | undefined) =>
    plans.map(get).find(v => typeof v === 'string' && v.trim().length > 0);
  return {
    destination: firstStr(p => p.destination),
    tripName:    firstStr(p => p.tripName),
    startDate:   firstStr(p => p.startDate),
    endDate:     firstStr(p => p.endDate),
    venues:   plans.flatMap(p => p.venues ?? []),
    days:     plans.flatMap(p => p.days ?? []),
    flights:  plans.flatMap(p => p.flights ?? []),
    stays:    plans.flatMap(p => p.stays ?? []),
    bookings: plans.flatMap(p => p.bookings ?? []),
    tips:     plans.flatMap(p => p.tips ?? []),
    packing:  plans.flatMap(p => p.packing ?? []),
    expenses: plans.flatMap(p => p.expenses ?? []),
  };
}

/**
 * Extracts each document in its own request (sequential), reporting the real
 * running totals after every document (`processed` documents done, `planSoFar`
 * merged) so the UI can show live counts. The single import credit is charged on
 * the first request only.
 */
export async function extractAllWithProgress(
  files: File[],
  onProgress: (processed: number, total: number, planSoFar: ExtractedTripPlan) => void,
  opts: { agencyId?: string } = {},
): Promise<{ plan: ExtractedTripPlan; aiCreditsLeft: number; premiumAiCreditsLeft: number; documentLanguage: string | null }> {
  if (files.length === 0) return { plan: { ...EMPTY_PLAN }, aiCreditsLeft: 0, premiumAiCreditsLeft: 0, documentLanguage: null };
  const plans: ExtractedTripPlan[] = [];
  let aiCreditsLeft = 0;
  let premiumAiCreditsLeft = 0;
  // First document that has enough words to name a language wins: a batch is
  // one person's own paperwork, and the tickets among it usually detect as null.
  let documentLanguage: string | null = null;
  for (let i = 0; i < files.length; i++) {
    // Report BEFORE the (slow) call: `i + 1` is the document being processed now,
    // with the counts merged from the documents already finished.
    onProgress(i + 1, files.length, mergeImportedPlans(plans));
    const res = await importApi.extract([files[i]], { consumeCredit: i === 0, agencyId: opts.agencyId });
    plans.push(res.plan);
    aiCreditsLeft = res.aiCreditsLeft;
    premiumAiCreditsLeft = res.premiumAiCreditsLeft;
    documentLanguage ??= res.documentLanguage;
  }
  return { plan: mergeImportedPlans(plans), aiCreditsLeft, premiumAiCreditsLeft, documentLanguage };
}

/** Non-zero running counts of what's been extracted, each with its i18n key. */
export function importPlanCounts(plan: ExtractedTripPlan): { key: string; count: number }[] {
  const out: { key: string; count: number }[] = [];
  const add = (count: number, key: string) => { if (count > 0) out.push({ key, count }); };
  add(plan.venues.length, 'import.placesSection');
  add(plan.days.reduce((s, d) => s + (d.activities?.length ?? 0), 0), 'import.activitiesSection');
  add(plan.flights.length, 'import.flightsSection');
  add(plan.stays.length, 'import.hotelsSection');
  add(plan.bookings.length, 'import.bookingsSection');
  add(plan.tips.length, 'import.tipsSection');
  add(plan.packing.reduce((s, p) => s + (p.items?.length ?? 0), 0), 'import.packingCount');
  add(plan.expenses.length, 'import.expensesSection');
  return out;
}
